import { Counterparty } from "../../../../common/types/counterparty";
import {
    RuleActionType,
    RuleConditionAmount,
    RuleConditionDate,
} from "../../../../common/types/transactionRule";
import { Category } from "../../../../common/types/category";
import { Entity } from "../../../../common/types/entity";
import { Account } from "../../../../common/types/domains/accounting/account";

export interface TransactionRuleFormSchema {
    groupId: string;
    conditions: TransactionRuleConditionFormSchema[];
    actions: TransactionRuleActionFormSchema[];
    applyToExistingTransactions: boolean;
    excludedTransactionsIds: number[];
}

export enum FormRuleConditionType {
    CATEGORY = "Category",
    SUGGESTED_CATEGORY = "Suggested category",
    AMOUNT = "Amount",
    BANK_DESCRIPTION = "Bank description",
    ACCOUNT = "Account",
    ENTITY = "Entity",
    COUNTERPARTY = "Vendor",
    DATE = "Date",
}

export enum DateConditionType {
    BEFORE = "Before",
    AFTER = "After",
    BETWEEN = "Between",
}

export enum AmountConditionType {
    GREATER_THAN = "Is above",
    LESS_THAN = "Is below",
    BETWEEN = "Is between",
}

export enum DescriptionConditionType {
    CONTAINS = "Contains",
    IS = "Is",
}

export interface TransactionRuleConditionFormSchema {
    id: string;
    conditionType?: FormRuleConditionType;
    amountIsCredit: boolean;
    amountConditionType: AmountConditionType;
    dateConditionType: DateConditionType;
    descriptionConditionType: DescriptionConditionType;
    amountSingleValue?: number;
    amountValueRange?: RuleConditionAmount;
    dateValueRange?: RuleConditionDate;
    descriptionValue?: string[];
    categoryValue?: Category;
    accountValue?: number[];
    entityValue?: number[];
    counterparty?: Counterparty;
}

export enum FormRuleActionType {
    CATEGORY = "Change category",
    ENTITY = "Move to entity",
    ACCOUNT_ID = "Assign to account",
    ACCOUNT_NAME = "Assign to account by name",
    CLASS = "Set class",
    MEMO = "Set note",
    COUNTERPARTY = "Set Vendor/Customer",
}

export const BusinessRuleFormActions = [
    FormRuleActionType.CATEGORY,
    FormRuleActionType.ENTITY,
    FormRuleActionType.MEMO,
    FormRuleActionType.COUNTERPARTY,
    FormRuleActionType.CLASS,
];

export const AccountingRuleFormActions = [FormRuleActionType.ACCOUNT_ID];

// Map between backend RuleActionType and frontend FormRuleActionType
export const mapRuleActionTypeToForm = (
    type: RuleActionType,
): FormRuleActionType => {
    switch (type) {
        case RuleActionType.CATEGORY:
            return FormRuleActionType.CATEGORY;
        case RuleActionType.ENTITY:
            return FormRuleActionType.ENTITY;
        case RuleActionType.ACCOUNT_ID:
            return FormRuleActionType.ACCOUNT_ID;
        case RuleActionType.ACCOUNT_NAME:
            return FormRuleActionType.ACCOUNT_NAME;
        case RuleActionType.CLASS:
            return FormRuleActionType.CLASS;
        case RuleActionType.MEMO:
            return FormRuleActionType.MEMO;
        case RuleActionType.COUNTERPARTY:
            return FormRuleActionType.COUNTERPARTY;
        default:
            throw new Error(`Unknown action type: ${type}`);
    }
};

export const mapRuleActionTypeToFormValue = (
    type: RuleActionType,
    value: string | number | string[],
): TransactionRuleActionFormValue => {
    switch (type) {
        case RuleActionType.CATEGORY:
            return {
                categoryId: value as string,
            };
        case RuleActionType.ENTITY:
            return {
                entityId: value as number,
            };
        case RuleActionType.ACCOUNT_ID:
            return {
                accountId: value as string,
            };
        case RuleActionType.ACCOUNT_NAME:
            return {
                accountName: value as string,
            };
        case RuleActionType.CLASS:
            return {
                classIds: value as string[],
            };
        case RuleActionType.MEMO:
            return {
                memo: value as string,
            };
        case RuleActionType.COUNTERPARTY:
            return {
                counterpartyId: value as string,
            };
        default:
            throw new Error(`Unknown action type: ${type}`);
    }
};

export const mapFormActionTypeToRule = (type: FormRuleActionType): string =>
    type.toLowerCase().replace(/ /g, "_");

export interface TransactionRuleActionFormValue {
    categoryId?: string;
    entityId?: number;
    accountId?: string;
    accountName?: string;
    classIds?: string[];
    taxonomy?: string[];
    memo?: string;
    counterpartyId?: string;
}
export interface TransactionRuleActionFormSchema {
    id: string;
    actionType?: FormRuleActionType;
    value?: TransactionRuleActionFormValue;
    categoryValue?: Category;
    entityValue?: Entity;
    counterpartyValue?: Counterparty;
    accountValue?: Account;
}
